import { ContentType, HasDcsId } from 'src/api/contentful/contentTypes.cloverdotcom';
import { ArrayFilters, ContentfulFilters, Filter } from 'src/api/contentful/fetchEntries';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { Status } from 'src/services/cms/typesUtil';
import { UseContentful, useContentful } from './UseContentful';

/**
 * Hook that gets CMS data filtered by dcsId collection
 * @param contentType content type
 * @param dcsIds collection of DCS ID
 * @param filters Contentful filters
 * @returns T data collection with response
 * @example
 *   const products = useCMSFilteredByDcsIds(ContentType.BUNDLE, ['FULL_SERVICE_DINING_BEST', 'FULL_SERVICE_DINING_GOOD']);
 */
export function useCMSFilteredByDcsIds<T extends HasDcsId>(contentType: ContentType, dcsIds: string[] = [], filters?: ContentfulFilters<T>): UseContentful<T> | undefined {
  return useContentful<T>(dcsIds.length ? {
    ...filters,
    filters: { fields: { dcsId: new Filter(ArrayFilters.INCLUDES_ONE_OF, dcsIds) } },
    contentType,
  } as ContentfulFilters<T> : undefined);
}

/**
 * Hook that gets CMS data filtered by dcsId
 * @param contentType content type
 * @param dcsId DCS ID
 * @param filters Contentful filters
 * @returns T data with response
 * @example
 *   const product = useCMSFilteredByDcsId(ContentType.BUNDLE, 'FULL_SERVICE_DINING_GOOD');
 */
export function useCMSFilteredByDcsId<T extends HasDcsId>(contentType: ContentType, dcsId?: string, filters?: ContentfulFilters<T>): {
  query: string;
  status: Status;
  data?: FlatEntry<T>;
  message?: string;
} | undefined {
  const res = useContentful<T>(dcsId ? {
    ...filters,
    filters: { fields: { dcsId } },
    contentType,
  } as ContentfulFilters<T> : undefined);

  return res ? { ...res, data: res?.data?.[0] } : undefined;
}
